import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Search, Package, Pencil } from 'lucide-react';
import { apiService } from '@/core/api/service';
import { useAppState } from '@/core/app-context';
import { EmptyTenantState } from '@/components/EmptyTenantState';
import { PageHeader } from '@/components/PageHeader';
import { SkeletonStats } from '@/components/SkeletonStats';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import type { Product } from '@/core/models';

export const Route = createFileRoute('/products')({
  head: () => ({
    meta: [{ title: 'Prodotti · Replya Internal Backoffice' }],
  }),
  component: ProductsPage,
});

type EnabledFilter = 'all' | 'enabled' | 'disabled';

function ProductEditDialog({
  product,
  isIndexed,
  indexSourceLabel,
  onOpenChange,
  onSubmit,
  isPending,
}: {
  product: Product | null;
  isIndexed: boolean | null;
  indexSourceLabel: string;
  onOpenChange: (open: boolean) => void;
  onSubmit: (isEnabled: boolean) => void;
  isPending: boolean;
}) {
  const [isEnabled, setIsEnabled] = useState(product?.isEnabled ?? true);

  return (
    <Dialog open={product !== null} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{product?.name}</DialogTitle>
          <DialogDescription>
            {product?.category}
            {product?.subcategory ? ` · ${product.subcategory}` : ''}
          </DialogDescription>
        </DialogHeader>
        {product && (
          <div className="space-y-4">
            {product.description && (
              <p className="text-sm text-muted-foreground">{product.description}</p>
            )}
            {isIndexed === false && isEnabled && (
              <p className="text-xs rounded-xl border border-amber-600/40 bg-amber-500/10 text-amber-600 p-3">
                Questo prodotto non è ancora nell'indice {indexSourceLabel}: il chatbot
                potrebbe non trovarlo o rispondere in modo impreciso. Valuta di
                disattivarlo finché non viene indicizzato.
              </p>
            )}
            <div className="flex items-center justify-between rounded-xl border border-border p-3">
              <div className="space-y-0.5">
                <Label htmlFor="product-enabled" className="text-sm">
                  Disponibile per il chatbot
                </Label>
                <p className="text-xs text-muted-foreground">
                  Se disattivato, il prodotto resta visibile e gestibile qui ma viene
                  nascosto temporaneamente al chatbot (es. dati incompleti o prodotto
                  non ancora indicizzato).
                </p>
              </div>
              <Switch
                id="product-enabled"
                checked={isEnabled}
                onCheckedChange={setIsEnabled}
              />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button
            type="button"
            disabled={isPending}
            onClick={() => onSubmit(isEnabled)}
            className="rounded-xl bg-brand hover:bg-brand-glow text-brand-foreground"
          >
            {isPending ? 'Salvataggio...' : 'Salva modifiche'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProductsPage() {
  const { selectedTenant } = useAppState();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [enabledFilter, setEnabledFilter] = useState<EnabledFilter>('all');
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);

  const enabledParam =
    enabledFilter === 'all' ? undefined : enabledFilter === 'enabled';

  const { data: products, isLoading } = useQuery({
    queryKey: ['products', selectedTenant?.tenantId, debouncedSearch, enabledFilter],
    queryFn: () =>
      apiService.listProducts(selectedTenant!.tenantId, debouncedSearch, enabledParam),
    enabled: !!selectedTenant,
  });

  const { data: indexStatus } = useQuery({
    queryKey: ['productIndexStatus', selectedTenant?.tenantId],
    queryFn: () => apiService.getProductIndexStatus(selectedTenant!.tenantId),
    enabled: !!selectedTenant,
  });

  // null = stato non determinabile (indice non raggiungibile o non ancora caricato):
  // meglio non marcare nulla che marcare tutto come non indicizzato.
  const isIndexed = (productId: string): boolean | null =>
    !indexStatus || !indexStatus.indexAvailable ? null : indexStatus.indexedIds.has(productId);
  const indexSourceLabel = indexStatus?.indexSource === 'mongo' ? 'MongoDB' : 'Qdrant';

  const updateMutation = useMutation({
    mutationFn: ({ productId, isEnabled }: { productId: string; isEnabled: boolean }) =>
      apiService.updateProduct(selectedTenant!.tenantId, productId, { isEnabled }),
    onSuccess: () => {
      toast.success('Prodotto aggiornato');
      setEditingProduct(null);
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Errore durante l'aggiornamento del prodotto");
    },
  });

  const handleSearch = () => {
    setDebouncedSearch(search);
  };

  if (!selectedTenant) {
    return <EmptyTenantState />;
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      <PageHeader
        title="Prodotti"
        subtitle={`Catalogo prodotti per ${selectedTenant.name}`}
      />

      <ProductEditDialog
        key={editingProduct?.id ?? 'edit-closed'}
        product={editingProduct}
        isIndexed={editingProduct ? isIndexed(editingProduct.id) : null}
        indexSourceLabel={indexSourceLabel}
        onOpenChange={(open) => !open && setEditingProduct(null)}
        onSubmit={(isEnabled) =>
          editingProduct &&
          updateMutation.mutate({ productId: editingProduct.id, isEnabled })
        }
        isPending={updateMutation.isPending}
      />

      <Card className="rounded-2xl border bg-surface">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Package className="h-4 w-4 text-brand" />
            Ricerca prodotti
          </CardTitle>
          <CardDescription>Cerca per nome, categoria o descrizione</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              placeholder="Cerca per nome, categoria..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="rounded-xl bg-surface-2 border-border sm:max-w-sm"
            />
            <Button
              onClick={handleSearch}
              className="rounded-xl bg-brand hover:bg-brand-glow text-brand-foreground"
            >
              <Search className="mr-2 h-4 w-4" />
              Cerca
            </Button>
            <Select
              value={enabledFilter}
              onValueChange={(v) => setEnabledFilter(v as EnabledFilter)}
            >
              <SelectTrigger className="rounded-xl bg-surface-2 border-border sm:w-64 sm:ml-auto">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="enabled">Mostra solo attivi in chat</SelectItem>
                <SelectItem value="disabled">Mostra solo disabilitati</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl border bg-surface">
        <CardHeader>
          <CardTitle className="text-base">Risultati</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <SkeletonStats count={3} />
          ) : (
            <div className="overflow-x-auto -mx-2 px-2">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Categoria</TableHead>
                    <TableHead>Indicizzato ({indexSourceLabel})</TableHead>
                    <TableHead>Stato chatbot</TableHead>
                    <TableHead className="w-16"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {products?.map((product) => (
                    <TableRow key={product.id}>
                      <TableCell className="font-medium whitespace-nowrap">{product.name}</TableCell>
                      <TableCell>{product.category}</TableCell>
                      <TableCell>
                        {isIndexed(product.id) === null ? (
                          <Badge variant="secondary">n/d</Badge>
                        ) : isIndexed(product.id) ? (
                          <Badge variant="default">Sì</Badge>
                        ) : (
                          <Badge variant="secondary">No</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {product.isEnabled ? (
                          <Badge variant="outline" className="text-xs text-emerald-600 border-emerald-600">
                            Attivo in chat
                          </Badge>
                        ) : (
                          <Badge
                            variant="outline"
                            className="text-xs text-amber-600 border-amber-600 bg-amber-500/10"
                          >
                            Non disponibile in chat
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="rounded-xl"
                          onClick={() => setEditingProduct(product)}
                          title="Modifica"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!products?.length && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground">
                        Nessun prodotto trovato
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
